<template>
  <OnboardingLayout
    :is-end="isEnd"
    :is-next-allowed="!!answers.fullName"
    :is-next-loading="isNextLoading"
    :is-prev-button-shown="false"
    @next-step="$emit('nextStep')"
    @prev-step="$emit('prevStep')"
  >
    <template #left>
      <div class="title">
        <h1>Tell us more about you.</h1>
      </div>

      <section>
        <h2>What is your full name?</h2>
        <FormInput
          ref="fullNameInput"
          v-model:value="fullName"
          type="text"
          placeholder="Full name…"
        />
      </section>
    </template>

    <template #right>
      <img :src="getImageUrl(imgName)" />
    </template>
  </OnboardingLayout>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  ref,
} from "vue";

import FormInput from "@/components/graph/definition/form/FormInput.vue";
import OnboardingLayout from "@/components/onboarding/OnboardingLayout.vue";
import { metrics } from "@/metrics";

export default defineComponent({
  components: {
    FormInput,
    OnboardingLayout,
  },
  props: {
    answers: {
      type: Object as PropType<{
        fullName: string | undefined;
      }>,
      required: true,
    },
    isEnd: { type: Boolean, required: false, default: false },
    isNextLoading: { type: Boolean, required: false, default: false },
  },
  emits: {
    answers: (_question: string | undefined, _answer: string | undefined) =>
      true,
    nextStep: () => true,
    prevStep: () => true,
  },
  setup(props, { emit }) {
    const fullNameInput = ref<InstanceType<typeof FormInput> | null>(null);
    const imgName = ref<string>("step_intro");
    metrics.segment.page("Onboarding", { step: "profile" });

    const fullName = computed<string>({
      get: () => props.answers.fullName ?? "",
      set: (v) => emit("answers", "fullName", v.trim() === "" ? undefined : v),
    });

    onMounted(async () => {
      await nextTick();
      window.requestAnimationFrame(() => {
        fullNameInput.value?.focus?.();
      });
    });

    const getImageUrl = (name: string) => {
      return new URL(`../../assets/onboarding/${name}.png`, import.meta.url)
        .href;
    };

    return {
      fullName,
      fullNameInput,
      imgName,
      getImageUrl,
    };
  },
});
</script>

<style scoped lang="scss">
.title {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  column-gap: 10px;
  margin-bottom: 6px;

  :deep(h1) {
    margin: 0;
  }
}

.title-icon {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  position: relative;
  top: 4px;
}
</style>
